import { CaretDownOutlined, DownSquareOutlined } from '@ant-design/icons'
import {
  loadable,
  TranslatedMessage,
  useCurrentWorkspace,
  useOrganization,
} from '@library/react-toolkit'
import { Alert, Button, Dropdown, Menu } from 'antd'
import { array, boolean, record, string } from 'fp-ts'
import { flow, pipe } from 'fp-ts/lib/function'
import * as React from 'react'
import { CSVLink } from 'react-csv'
import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import { usePBLPermission } from '../../redux'
import ButtonGroup from '../ButtonGroup'
import ContributorButton from '../ContributorButton'
import { TableHeader } from '../styled'
import { startWizardAction } from '../Upload/types'
import ExportButton from './ExportButton'
import ModeSelector from './ModeSelector'
import {
  useModalVisible,
  useMode,
  useSelectedBills,
  useSendSupplierBills,
  useSetSelectedBills,
} from './queries'
import { NotificationMethod, sendBillsAction } from './types'
import * as axios from '../../axios-extended'
import * as t from 'io-ts'
import { useMutation } from 'react-query'

const HeaderActions = styled.div`
  display: flex;
  justify-content: space-between;

  & > div:first-child > button {
    margin-right: 8px;
  }
`

const HeaderButtons = styled.div`
  display: flex;
  justify-content: flex-end;

  & > * {
    margin-right: 8px;
  }
`

const NotifyWith = ({
  method,
  id,
  ids,
}: {
  method: NotificationMethod
  id: string
  ids: number[]
}) => {
  const dispatch = useDispatch()
  const sendSupplierBills = useSendSupplierBills()
  const mode = useMode()
  return (
    <Menu.Item
      onClick={() => {
        mode === 'organization'
          ? dispatch(sendBillsAction({ method, ids }))
          : sendSupplierBills.mutate(ids)
      }}
    >
      <FormattedMessage id={id} />
    </Menu.Item>
  )
}

const recordKeysAsNumbers = (r: Record<string, unknown>) =>
  pipe(
    r,
    record.keys,
    array.map(a => parseInt(a, 10))
  )

const BillsTableHeader = () => {
  const organization = useOrganization()

  const downloadThings = useMutation((ids: number[]) =>
    pipe(
      axios.post(`/prosperbill/api/invoices/${organization?.name}/csv`, ids, {
        decoder: t.unknown,
      }),
      axios.throwLeft
    )()
  )

  const mode = useMode()
  const selectedBills = useSelectedBills()
  const setSelectedBills = useSetSelectedBills()
  const permission = usePBLPermission()
  const selectedIDs = recordKeysAsNumbers(selectedBills)
  const sendLoading = useSelector(s => loadable.isLoading(s.bills.sendResult))
  const importLoading = useSelector(s =>
    loadable.isLoading(s.bills.importResult)
  )
  const dispatch = useDispatch()
  const [_, setAddModalVisible] = useModalVisible()

  const noSelection = record.isEmpty(selectedBills)
  const anyPaid = pipe(
    selectedBills,
    record.foldMap(string.Ord)(boolean.MonoidAny)(b => b.status === 'PAID')
  )

  const downloadURL = React.useMemo(() => {
    const p = new URLSearchParams()
    selectedIDs.forEach(id => p.append('ids', id.toString()))

    return `/prosperbill/api/invoices/${organization?.name}/csv?${p.toString()}`
  }, [organization?.name, selectedIDs])

  const intl = useIntl()
  const ws = useCurrentWorkspace()
  return (
    <TableHeader
      title={
        <>
          {mode === 'organization'
            ? intl.formatMessage(
                { id: 'organizationInvoices' },
                {
                  name: ws?.type === 'organization' ? ws.organization.name : '',
                }
              )
            : intl.formatMessage({ id: 'supplierInvoices' })}
          <Dropdown arrow overlay={<ModeSelector />}>
            <DownSquareOutlined
              style={{ color: 'green', marginLeft: '10px' }}
            />
          </Dropdown>
        </>
      }
    >
      <HeaderActions>
        {mode === 'organization' ? (
          <div>
            <Button
              disabled={noSelection || anyPaid}
              onClick={() => setSelectedBills({})}
            >
              <FormattedMessage id="clear" />
            </Button>
            <FormattedMessage
              id="rowsSelected"
              values={{ n: Object.keys(selectedBills).length }}
            />
            {anyPaid && (
              <Alert
                type="error"
                message={<TranslatedMessage id="cannotErasePaidInvoices" />}
                style={{ marginTop: '8px' }}
              />
            )}
          </div>
        ) : (
          <div />
        )}
        <HeaderButtons>
          <ButtonGroup margin={2}>
            <ExportButton />
            <Dropdown
              disabled={noSelection || permission !== 'CONTRIBUTOR'}
              overlay={
                <Menu>
                  <NotifyWith method="SMS" id="bySMS" ids={selectedIDs} />
                  <NotifyWith method="EMAIL" id="byEmail" ids={selectedIDs} />
                  <NotifyWith
                    method="APP"
                    id="byApplication"
                    ids={selectedIDs}
                  />
                </Menu>
              }
            >
              <ContributorButton disabled={noSelection} loading={sendLoading}>
                <FormattedMessage id="notify" />
              </ContributorButton>
            </Dropdown>
            <Button href={downloadURL}>
              <FormattedMessage id="export" />
            </Button>
            <Dropdown
              overlay={
                <Menu>
                  <Menu.Item
                    key="1"
                    onClick={flow(startWizardAction, dispatch)}
                  >
                    <FormattedMessage id="batchImport" />
                  </Menu.Item>
                  <Menu.Item key="2" onClick={() => setAddModalVisible(true)}>
                    <FormattedMessage id="singleInvoice" />
                  </Menu.Item>
                </Menu>
              }
            >
              <Button loading={importLoading} type="primary">
                <FormattedMessage id="addInvoices" /> <CaretDownOutlined />
              </Button>
            </Dropdown>
          </ButtonGroup>
        </HeaderButtons>
      </HeaderActions>
    </TableHeader>
  )
}

export default BillsTableHeader
